import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useState } from "react";
import classNames from "classnames";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useRevokePersonalAccessTokenMutation } from "@api/personalAccessTokens.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { McpCliTokenRow } from "../mcpCliTokensViewConfig";
import styles from "../McpCliSettings.module.scss";

export const TokenActionsCell = memo(
    ({ row }: CellContext<ViewTableFeatures, McpCliTokenRow, string>) => {
        const revokeMutation = useRevokePersonalAccessTokenMutation();
        const { error: toastError } = useToaster();
        const [showConfirmation, setShowConfirmation] = useState(false);
        const token = row.original;

        const confirmRevoke = useCallback(async () => {
            try {
                await revokeMutation.mutateAsync(token.id);
                setShowConfirmation(false);
            } catch {
                toastError("Failed to revoke token");
            }
        }, [revokeMutation, toastError, token.id]);

        if (token.revokedAt) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell
                captureClicks
                className={classNames(
                    styles.actionCell,
                    viewTableStyles.showOnHover,
                )}
            >
                <ButtonWithLoader
                    variant="primary"
                    size="sm"
                    loading={revokeMutation.isPending}
                    onClick={() => setShowConfirmation(true)}
                >
                    Revoke
                </ButtonWithLoader>
                <ConfirmationModal
                    show={showConfirmation}
                    onHide={() => setShowConfirmation(false)}
                    closeButton
                    title="Revoke personal access token?"
                    question={`This will permanently revoke "${token.name}". This cannot be undone.`}
                    onConfirm={confirmRevoke}
                    onReject={() => setShowConfirmation(false)}
                    yes="Revoke"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </ViewTableCell>
        );
    },
);
